import { rbac } from '@plannrcrm/rbac';

const DENIED_MESSAGE = 'You do not have permission to access this area.';

function denyAccess(redirect) {
  const notify = () => {
    if (window.$nuxt) {
      window.$nuxt.$emit('notification', DENIED_MESSAGE, 'warning', true);
    } else {
      setTimeout(notify, 100);
    }
  };
  setTimeout(notify, 300);
  return redirect('/dashboard');
}

/**
 * Middleware factory that allows access if the user has at least one of the
 * given permissions. Accepts a single permission string or an array of strings.
 *
 * @param {string|string[]} required
 *
 * @example
 * middleware: ['auth', 'noClients', hasPermission('contacts:read')]
 * middleware: ['auth', 'noClients', hasPermission(['contacts:read', 'circles:read'])]
 */
export function hasPermission(required) {
  return function ({ redirect }) {
    const granted = Array.isArray(required) ? required.some((p) => rbac.can(p)) : rbac.can(required);
    if (!granted) {
      return denyAccess(redirect);
    }
  };
}

/**
 * Middleware factory that allows access only if the user has every one of the
 * given permissions.
 *
 * @param {string[]} required
 *
 * @example
 * middleware: ['auth', 'noClients', hasAllPermissions(['contacts:read', 'clients:read'])]
 */
export function hasAllPermissions(required) {
  return function ({ redirect }) {
    if (!rbac.canAll(required)) {
      return denyAccess(redirect);
    }
  };
}

/**
 * Middleware factory that sends the user to a sibling page when they cannot use this page's
 * own resource but can use the sibling's.
 *
 * This has to run as middleware rather than in the page's `created()` hook. `$router.replace`
 * does not halt the current page: Vue carries on mounting it, its filter bar fires the request
 * for the resource the user has no permission for, and the resulting 403 raises a toast that
 * lands on the page they were redirected to. Middleware runs before the page component is ever
 * instantiated, so no request goes out.
 *
 * @param {string} required        permission this page's own data needs
 * @param {string} fallbackPath    where to send the user instead
 * @param {string} fallbackRequired permission the fallback page's data needs
 *
 * @example
 * middleware: [
 *   'auth',
 *   'noClients',
 *   hasPermission(['automations:read', 'automations-blueprints:read']),
 *   redirectWhenCannot('automations:read', '/automations/blueprints', 'automations-blueprints:read'),
 * ]
 */
export function redirectWhenCannot(required, fallbackPath, fallbackRequired) {
  return function ({ redirect }) {
    if (rbac.cannot(required) && rbac.can(fallbackRequired)) {
      return redirect(fallbackPath);
    }
  };
}
